import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useDomains, RECOMMENDATION_LABEL, type DomainEntry, type DomainSystem } from '@/api/domains';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { Journey, Ok, Pct } from '@/components/domains/DomainsKit';
import { TierBox } from '@/components/TierBox';
import { useClientScope } from '@/contexts/ClientScopeContext';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, companyPath, domainPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Domains — every client domain from dmarcian, CIPP (Domain Analyser) and IT Glue (Domains + the daily
 * Email Security asset), with where it's missing. Rule (decided 2026-10-16): every domain belongs in CIPP
 * and IT Glue, and in dmarcian when the client is Premium or Enterprise (Autotask Classification).
 * DMARC state: dmarcian, else IT Glue Email Security (accurate, daily), else CIPP.
 */

type Show = 'all' | 'gaps' | 'gap-itglue' | 'gap-cipp' | 'gap-dmarcian' | 'monitored' | 'unmonitored' | 'unmapped'
  | 'ready' | 'none' | 'quarantine' | 'reject' | 'missing' | 'problems';

const FILTERS: { id: Show; label: string; row: 'coverage' | 'dmarc'; test: (e: DomainEntry) => boolean }[] = [
  { id: 'all',          label: 'All',                 row: 'coverage', test: () => true },
  { id: 'gaps',         label: 'Any gap',             row: 'coverage', test: (e) => e.missing.length > 0 },
  { id: 'gap-itglue',   label: 'Not in IT Glue',      row: 'coverage', test: (e) => e.missing.includes('itglue') },
  { id: 'gap-cipp',     label: 'Not in CIPP',         row: 'coverage', test: (e) => e.missing.includes('cipp') },
  { id: 'gap-dmarcian', label: 'dmarcian required',   row: 'coverage', test: (e) => e.missing.includes('dmarcian') },
  { id: 'monitored',    label: 'In dmarcian',         row: 'coverage', test: (e) => e.in.dmarcian },
  { id: 'unmonitored',  label: 'Not in dmarcian',     row: 'coverage', test: (e) => !e.in.dmarcian },
  { id: 'unmapped',     label: 'Unmapped',            row: 'coverage', test: (e) => e.company.id == null },
  { id: 'ready',        label: 'Ready to tighten',    row: 'dmarc', test: (e) => !!e.dmarcian?.readyToEnforce },
  { id: 'none',         label: 'p=none',              row: 'dmarc', test: (e) => e.stage === 'monitoring' },
  { id: 'quarantine',   label: 'Quarantine',          row: 'dmarc', test: (e) => e.stage === 'quarantine' },
  { id: 'reject',       label: 'Reject',              row: 'dmarc', test: (e) => e.stage === 'reject' },
  { id: 'missing',      label: 'No DMARC',            row: 'dmarc', test: (e) => e.stage === 'missing' },
  { id: 'problems',     label: 'dmarcian problems',   row: 'dmarc', test: (e) => !!e.dmarcian && (e.dmarcian.failingSenders > 0 || e.dmarcian.openIssues > 0 || e.dmarcian.spf.ok === false) },
];

const PREF = 'cosmos:domains:show';
const STAGE_RANK = { missing: 0, monitoring: 1, quarantine: 2, reject: 3 } as const;
const SOURCE_LABEL: Record<DomainSystem, string> = { dmarcian: 'dmarcian', itglue: 'IT Glue', cipp: 'CIPP' };

export default function DomainsPage() {
  const { data, isLoading, error, isFetching } = useDomains({ scoped: true });
  const { scope, setScope } = useClientScope();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [show, setShowState] = useState<Show>(() => {
    try { const v = localStorage.getItem(PREF) as Show | null; return v && FILTERS.some((f) => f.id === v) ? v : 'all'; } catch { return 'all'; }
  });
  const [q, setQ] = useState('');
  const setShow = (s: Show) => { setShowState(s); try { localStorage.setItem(PREF, s); } catch { /* storage unavailable */ } };

  useEffect(() => {
    const s = params.get('show') as Show | null;
    if (!s) return;
    if (FILTERS.some((f) => f.id === s)) setShow(s);
    setParams({}, { replace: true });
  }, [params, setParams]);

  const all = useMemo(() => [...(data?.entries ?? [])].sort((a, b) =>
    b.missing.length - a.missing.length || Number(!!b.dmarcian?.readyToEnforce) - Number(!!a.dmarcian?.readyToEnforce)
    || STAGE_RANK[a.stage] - STAGE_RANK[b.stage] || a.domain.localeCompare(b.domain)), [data]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, all.filter(f.test).length])) as Record<Show, number>, [all]);
  const s = q.trim().toLowerCase();
  const rows = useMemo(() => {
    const f = FILTERS.find((x) => x.id === show) ?? FILTERS[0];
    return all.filter(f.test).filter((e) => !s || e.domain.includes(s) || (e.company.name ?? '').toLowerCase().includes(s));
  }, [all, show, s]);

  const chips = (row: 'coverage' | 'dmarc') => (
    <div className="flex flex-wrap gap-1 rounded-lg bg-muted p-1" style={{ width: 'fit-content' }}>
      <span className="self-center px-2 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{row === 'coverage' ? 'Systems' : 'DMARC'}</span>
      {FILTERS.filter((f) => f.row === row).map((f) => (
        <button key={f.id} type="button" onClick={() => setShow(f.id)}
          className={cn('rounded-md px-3 py-1 text-xs font-medium', show === f.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
          {f.label} <span className="ml-1 tabular-nums text-muted-foreground">{counts[f.id]}</span>
        </button>
      ))}
    </div>
  );

  return (
    <DesktopOnly title="Domains is available on desktop" message="Open Cosmos on a larger screen to view domains.">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <PageHeader title="Domains" count={data ? rows.length : null} isFetching={isFetching}
          subtitle={data && <>Client domains across dmarcian, CIPP and IT Glue{data.fetchedAt && <> · read {fmtRelative(data.fetchedAt)}</>}</>}
          actions={<SearchInput withIcon placeholder="Domain or client" className="w-64" value={q} onValueChange={setQ} />} />

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : error ? (
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
        ) : data ? (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 min-[1400px]:grid-cols-6">
              <StatCard label="Domains" value={all.length} sub={`${counts.monitored} in dmarcian`} onClick={() => setShow('all')} />
              <StatCard label="Gaps" value={counts.gaps} sub="missing from a system" tone={counts.gaps ? 'destructive' : 'success'} onClick={() => setShow('gaps')} />
              <StatCard label="dmarcian required" value={counts['gap-dmarcian']} sub="Premium / Enterprise, not in dmarcian" tone={counts['gap-dmarcian'] ? 'warning' : 'success'} onClick={() => setShow('gap-dmarcian')} />
              <StatCard label="No DMARC" value={counts.missing} sub="no record" tone={counts.missing ? 'destructive' : 'success'} onClick={() => setShow('missing')} />
              <StatCard label="Ready to tighten" value={counts.ready} sub={`≥ ${data.readyPct}% for ${data.readyDays}+ days`} tone={counts.ready ? 'warning' : 'success'} onClick={() => setShow('ready')} />
              <StatCard label="At reject" value={counts.reject} sub="fully enforced" tone="success" onClick={() => setShow('reject')} />
            </div>

            <div className="mt-4 space-y-1.5">{chips('coverage')}{chips('dmarc')}</div>

            <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
              <table className="w-full text-sm" style={{ minWidth: 1100 }}>
                <thead className="whitespace-nowrap bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Domain</th>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">DMARC</th>
                    <th className="px-3 py-2 font-medium">Systems</th>
                    <th className="px-3 py-2 text-right font-medium">Mail (7d)</th>
                    <th className="px-3 py-2 font-medium">Compliance</th>
                    <th className="px-3 py-2 font-medium">Next step</th>
                    <th className="px-3 py-2 font-medium">SPF</th>
                    <th className="px-3 py-2 font-medium">DKIM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {rows.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">
                      {scope && all.length === 0 ? (
                        <div className="space-y-2">
                          <p><span className="font-medium text-foreground">{scope.name}</span> has no domains in dmarcian, CIPP or IT Glue.</p>
                          <Button size="sm" variant="ghost" onClick={() => setScope(null)}>Show all clients</Button>
                        </div>
                      ) : 'Nothing matches.'}
                    </td></tr>
                  )}
                  {rows.map((e) => {
                    const d = e.dmarcian;
                    const dash = <span className="text-xs text-muted-foreground">—</span>;
                    return (
                      <tr key={e.domain} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate(domainPath(e.domain))}>
                        <td className="px-3 py-2">
                          <div className="max-w-[220px] truncate font-medium text-foreground" title={e.domain}>{e.domain}</div>
                          {d && !d.active && <div className="text-[11px] text-muted-foreground">no dmarcian reports in 7 days</div>}
                        </td>
                        <td className="max-w-[200px] px-3 py-2 text-xs" onClick={(ev) => ev.stopPropagation()}>
                          <div className="flex items-center gap-1.5">
                            <TierBox tier={e.tier} size="xs" />
                            {e.company.id != null ? <Link to={companyPath(e.company.id)} className="truncate text-foreground hover:underline">{e.company.name}</Link> : <span className="italic text-muted-foreground">unmapped</span>}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <Journey stage={e.stage} />
                          {e.stageSource && e.stageSource !== 'dmarcian' && <span className="ml-1.5 text-[10.5px] text-muted-foreground">via {SOURCE_LABEL[e.stageSource]}</span>}
                        </td>
                        <td className="px-3 py-2"><Systems e={e} /></td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">{d ? d.volume7d.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2">{d ? <Pct value={d.compliancePct} /> : dash}</td>
                        <td className="px-3 py-2 text-xs">
                          {d?.readyToEnforce ? <span className="whitespace-nowrap rounded-full bg-warning-soft px-2 py-0.5 font-semibold text-warning">Ready to tighten</span>
                            : <span className="text-muted-foreground">{nextStep(e)}</span>}
                        </td>
                        <td className="px-3 py-2"><Ok ok={e.spf} label={d?.spf.label ?? null} /></td>
                        <td className="px-3 py-2"><Ok ok={e.dkim} label={d?.dkim.label ?? null} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Every client domain belongs in CIPP and IT Glue, and in dmarcian for Premium and Enterprise customers (Autotask Classification).
              DMARC / SPF / DKIM come from dmarcian, else IT Glue's daily Email Security check, else CIPP's Domain Analyser.
              "Ready to tighten" = dmarcian recommends the next step, ≥ {data.readyPct}% of 7 days' mail passed DMARC, {data.readyDays}+ days at the current policy
              (<Link to={ROUTES.admin.domains} className="hover:underline">Admin → Domains</Link>).
            </p>
          </>
        ) : null}
      </div>
    </DesktopOnly>
  );
}

function nextStep(e: DomainEntry): string {
  const d = e.dmarcian;
  if (d?.recommendation) return RECOMMENDATION_LABEL[d.recommendation] ?? d.recommendation;
  if (e.missing.includes('dmarcian')) return 'Add to dmarcian';
  if (e.missing.includes('itglue')) return 'Add to IT Glue';
  if (e.missing.includes('cipp')) return 'Add to the M365 tenant';
  if (e.stage === 'missing') return 'Publish a DMARC record';
  return '—';
}

/** Three small chips: present (green), missing where required (red), not required (muted). */
function Systems({ e }: { e: DomainEntry }) {
  const chip = (sys: DomainSystem, label: string) => {
    const present = e.in[sys];
    const missing = e.missing.includes(sys);
    return (
      <span key={sys} title={present ? `In ${label}` : missing ? `Missing from ${label}` : `Not in ${label} (not required)`}
        className={cn('rounded px-1.5 py-px text-[10.5px] font-semibold',
          present ? 'bg-success-soft text-success' : missing ? 'bg-destructive-soft text-destructive' : 'bg-muted text-muted-foreground/70')}>
        {label}
      </span>
    );
  };
  return <span className="inline-flex gap-1 whitespace-nowrap">{chip('cipp', 'CIPP')}{chip('itglue', 'ITG')}{chip('dmarcian', 'dmarcian')}</span>;
}
